import * as React from 'react';
import {Dispatch, useState} from "react";

type PickUpModel = {
    setStep: Dispatch<any>,
    floor: number,
    setFloor: Dispatch<any>,
    trashNearHouse: boolean,
    setTrashNearHouse: Dispatch<any>,
    elevatorExist: boolean,
    setElevatorExist: Dispatch<any>,
    apiUrl: string,
    stepsBarRef: any,
    deliveryDate: string,
    selectedCity: string,
    selectedSize: string,
    bagsCount: number
};

interface Floor {
    label: string;
    description: string;
    price: string;
    floorId: number;
    icon: string;
}

interface Price {
    totalPrice: number,
    deliveryTodayPrice: number,
    floorPrice: number,
    garbagePackagePrice: number,
    bagsPrice: number
}

function fetchFloors(apiUrl: string, signal: AbortSignal) {
    return new Promise<{
        timeIntervals: Floor[]
    }>((resolve, reject) => {
        const options = {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
            signal
        };
        fetch(apiUrl + 'floors', options)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((timeIntervals) => {
                resolve({timeIntervals});
            })
            .catch(reject);
    });
}

function fetchPrice(apiUrl: string, city: string, size: string, deliveryDate: string, floor: number, bagsCount: number, signal: AbortSignal) {
    return new Promise<{
        finalPrice: Price
    }>((resolve, reject) => {
        const options = {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
            signal
        };
        fetch(apiUrl + 'price/' + encodeURIComponent(city) + '/' + encodeURIComponent(size) + '/' + encodeURIComponent(deliveryDate) + '/' + floor + '/' + bagsCount, options)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((finalPrice) => {
                resolve({finalPrice});
            })
            .catch(reject);
    });
}


export const PickUpStep = ({
                               setStep,
                               floor,
                               setFloor,
                               trashNearHouse,
                               setTrashNearHouse,
                               elevatorExist,
                               setElevatorExist,
                               apiUrl,
                               stepsBarRef,
                               deliveryDate,
                               selectedCity,
                               selectedSize,
                               bagsCount
                           }: PickUpModel) => {

    const [floorError, setFloorError] = useState(false);
    const requestAbortController = React.useRef<AbortController | null>(null);
    const priceAbortController = React.useRef<AbortController | null>(null);
    const [floorsList, setFloorsList] = useState<Floor[]>([]);
    const [finalPrice, setFinalPrice] = useState<Price | null>(null);

    const selectedFloor = floorsList.find((item) => item.floorId === floor);

    const handleNextClick = () => {
        let valid = floor !== 0;

        if (valid) {
            setFloorError(false);
            setStep(3);
            stepsBarRef.current?.scrollIntoView({behavior: 'smooth'});
        } else {
            setFloorError(true);
        }
    };


    const handleBackClick = () => {
        setStep(1);
    };

    const handleFloorSelection = (event: any) => {
        setFloor(parseInt(event.target.value));
        setFloorError(false);

        if (parseInt(event.target.value) !== 1) {
            setTrashNearHouse(false);
        }

        if (parseInt(event.target.value) === 1) {
            setElevatorExist(false);
        }
    };

    const handleLiftSelection = (event: any) => {
        setElevatorExist(event.target.checked);
    };

    const handleNearHome = (event: any) => {
        setTrashNearHouse(event.target.checked);
    };


    const fetchFloorsApi = () => {
        const controller = new AbortController();
        fetchFloors(apiUrl, controller.signal)
            .then(({timeIntervals}) => {
                setFloorsList(timeIntervals);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });

        requestAbortController.current = controller;
    };

    React.useEffect(() => {
        fetchFloorsApi();
        // abort request on unmount
        return () => requestAbortController.current?.abort();
    }, []);

    React.useEffect(() => {
        const controller = new AbortController();
        // Same endpoint and "tomorrow" default DeliveryInformationStep uses, so the total
        // stays identical (incl. today-delivery surcharge) when navigating between the two steps.
        // Before a floor is chosen, price with floor 1 so the price panel is already visible.
        fetchPrice(apiUrl, selectedCity, selectedSize, deliveryDate || 'tomorrow', floor || 1, bagsCount, controller.signal)
            .then(({finalPrice}) => {
                setFinalPrice(finalPrice);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });

        priceAbortController.current = controller;

        return () => priceAbortController.current?.abort();
    }, [floor, deliveryDate]);

    return (
        <>
            <div className="clearfix"></div>
            <div id="pickup-section-grid" className="grid">
                <div className="col-70">
                    <div>
                        <div className="pickup-info">
                            <span>Atliekų vieta</span>
                            <span className="info-badge">Kaina atsinaujina realiu laiku</span>
                        </div>
                        <h3>Kur yra atliekos?</h3>
                        <ul className={"choices-list"}>
                            {floorsList.map(function (item) {
                                return <li key={item.floorId}
                                           className={floor === item.floorId ? "active" : ""}
                                           style={floorError ? {borderColor: "red"} : {}}>
                                    <label htmlFor={"ra" + item.floorId} aria-label={item.label}></label>
                                    <div className="choice-description">
                                        <div className="choice-icon" dangerouslySetInnerHTML={{__html: item.icon}}/>
                                        <div>
                                            <div className="choice-title">{item.label}</div>
                                            <div className="choice-sub">{item.description}</div>
                                        </div>
                                    </div>
                                    <div className="choice-price-block">
                                        <div className="choice-price">+{item.price} €</div>
                                        <input type="radio" name="height" value={item.floorId}
                                               onClick={handleFloorSelection}
                                               checked={floor === item.floorId}
                                               id={"ra" + item.floorId}/>
                                        <div className="radio">
                                        </div>
                                    </div>
                                </li>


                            })}
                        </ul>

                        <div className="pickup-info">
                            <span>Papildomai</span>
                        </div>
                        <h3>Papildoma informacija apie privažiavimą</h3>
                        <ul className={"choices-list"}>
                            <li>
                                <label htmlFor="raa1" aria-label="Atliekos šalia namo"></label>
                                <div className="choice-description">
                                    <div className="choice-icon">🌳</div>
                                    <div>
                                        <div className="choice-title">Atliekos šalia namo</div>
                                        <div className="choice-sub">Pažymėkite, jei atliekos lauke ar kieme</div>
                                    </div>
                                </div>
                                <div className="choice-price-block">
                                    <input type="checkbox" name="near-home" value={true} id="raa1"
                                           disabled={floor !== 1}
                                           checked={trashNearHouse} onChange={handleNearHome}/>
                                    <div className="checkbox">
                                    </div>
                                </div>
                            </li>
                            <li>
                                <label htmlFor="raa2" aria-label="Yra liftas"></label>
                                <div className="choice-description">
                                    <div className="choice-icon">🛗</div>
                                    <div>
                                        <div className="choice-title">Yra liftas</div>
                                        <div className="choice-sub">Pažymėkite, jei pastate galima naudotis liftu
                                        </div>
                                    </div>
                                </div>
                                <div className="choice-price-block">
                                    <input type="checkbox" name="lift" value={true} id="raa2" disabled={floor === 1}
                                           checked={elevatorExist} onChange={handleLiftSelection}/>
                                    <div className="checkbox">
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {finalPrice !== null && (
                    <div className="col-30">
                        <div>
                            <h3 className="price-panel-title">Kaina</h3>
                            <div className="card">
                                <div className="stats">
                                    <div className="stat">
                                        <div className="label">Atliekų kaina</div>
                                        <div className="stat-value ready">{finalPrice.garbagePackagePrice}€</div>
                                    </div>
                                    {floor !== 0 && selectedFloor && (
                                        <div className="stat">
                                            <div className="label">Pasirinktas aukštas</div>
                                            <div className="stat-value ready">{selectedFloor.label}</div>
                                        </div>
                                    )}
                                    {finalPrice.floorPrice > 0 && (
                                        <div className="stat">
                                            <div className="label">Aukšto / lifto kaina</div>
                                            <div className="stat-value ready">+{finalPrice.floorPrice}€</div>
                                        </div>
                                    )}
                                    {finalPrice.deliveryTodayPrice > 0 && (
                                        <div className="stat">
                                            <div className="label">Pristatymas šiandien</div>
                                            <div className="stat-value ready">+{finalPrice.deliveryTodayPrice}€</div>
                                        </div>
                                    )}
                                    {finalPrice.bagsPrice > 0 && (
                                        <div className="stat">
                                            <div className="label">Maišų kaina</div>
                                            <div className="stat-value ready">+{finalPrice.bagsPrice}€</div>
                                        </div>
                                    )}
                                    {floor > 1 && (
                                        <div className="stat">
                                            <div className="label">Liftas</div>
                                            <div className="stat-value ready">{elevatorExist ? "Taip" : "Ne"}</div>
                                        </div>
                                    )}
                                    {floor === 1 && (
                                        <div className="stat">
                                            <div className="label">Atliekos prie namo</div>
                                            <div className="stat-value ready">{trashNearHouse ? "Taip" : "Ne"}</div>
                                        </div>
                                    )}
                                </div>
                                <div className="notice">
                                    <div className="notice-text">
                                        <strong>Bendra preliminari kaina</strong>: <span
                                        key={finalPrice.totalPrice}
                                        className="stat-value green ready price-pulse"
                                        id="totalPrice">{finalPrice.totalPrice}€</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="mobile-sticky-footer">
                {finalPrice !== null && (
                    <div className="mobile-total-price-box">
                        <span>Bendra preliminari kaina</span>
                        <strong key={finalPrice.totalPrice} className="price-pulse">{finalPrice.totalPrice}€</strong>
                    </div>
                )}
                <div className="navigation-block">
                    <button className="btn btn-secondary btn-normal btn-left" onClick={handleBackClick}>← Atgal
                    </button>
                    <button className="btn primary-btn btn-normal btn-right" onClick={handleNextClick}>Tęsti →
                    </button>
                </div>
            </div>
        </>
    );
}
